// P55 — Onglet « Santé des services » de la page Audit.
//
// Vérifie en direct que tous les services autour de l'app fonctionnent :
// base de données, SMTP, Redis, stockage, outils de backup, scheduler.
//
// - Bouton « Refresh » → re-teste tout (GET /api/v1/diagnostics)
// - Bouton « Envoyer un email de test » → mail de vérification à l'user
//   connecté (POST /api/v1/diagnostics/smtp/test)
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Mail,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { useT } from "@/lib/i18n";
import { ht } from "@/lib/ht";
import { API_BASE } from "@/lib/apiBase";

interface CheckResult {
  key: string;
  label: string;
  status: "ok" | "warning" | "error";
  message: string;
  details: Record<string, unknown>;
}

interface DiagnosticsReport {
  status: "ok" | "warning" | "error";
  checked_at: string;
  duration_ms: number;
  counts: { ok: number; warning: number; error: number };
  checks: CheckResult[];
}

const STATUS_STYLE: Record<string, { icon: typeof CheckCircle; className: string; label: string }> = {
  ok: { icon: CheckCircle, className: "text-green-600", label: "OK" },
  warning: { icon: AlertTriangle, className: "text-amber-500", label: "Avertissement" },
  error: { icon: XCircle, className: "text-red-600", label: "Erreur" },
};

export default function DiagnosticsPanel() {
  const t = useT();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [refreshing, setRefreshing] = useState(false);

  const report = useQuery({
    queryKey: ["diagnostics"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/diagnostics`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as DiagnosticsReport;
    },
    // On ne rafraîchit pas automatiquement : l'utilisateur clique sur Refresh
    // (un ping SMTP à chaque polling serait inutile).
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const smtpTest = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/v1/diagnostics/smtp/test`, {
        method: "POST",
        credentials: "include",
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      return body as { status: string; recipient: string; message: string };
    },
    onSuccess: (data) => {
      toast({
        title: t("diagnostics.toast.smtp_sent"),
        description: data.message,
        variant: "success",
        duration: 8000,
      });
      qc.invalidateQueries({ queryKey: ["diagnostics"] });
    },
    onError: (err) => {
      toast({
        title: t("diagnostics.toast.smtp_failed"),
        description: (err as Error).message,
        variant: "error",
        duration: 10000,
      });
      qc.invalidateQueries({ queryKey: ["diagnostics"] });
    },
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await qc.invalidateQueries({ queryKey: ["diagnostics"] });
      await qc.refetchQueries({ queryKey: ["diagnostics"] });
    } finally {
      setRefreshing(false);
    }
  };

  const data = report.data;
  const overall = data ? STATUS_STYLE[data.status] : null;

  return (
    <div className="space-y-4">
      {/* En-tête + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="text-lg font-semibold">{t("diagnostics.title")}</h2>
            <p className="text-xs text-muted-foreground">
              {t("diagnostics.subtitle")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing || report.isFetching}
            data-testid="diagnostics-refresh"
          >
            <RefreshCw
              className={`mr-1 h-3.5 w-3.5 ${report.isFetching ? "animate-spin" : ""}`}
            />
            {t("diagnostics.refresh")}
          </Button>
          <Button
            size="sm"
            onClick={() => smtpTest.mutate()}
            disabled={smtpTest.isPending}
            data-testid="diagnostics-smtp-test"
          >
            <Mail className="mr-1 h-3.5 w-3.5" />
            {smtpTest.isPending ? t("diagnostics.sending") : t("diagnostics.send_test_email")}
          </Button>
        </div>
      </div>

      {/* Bandeau global */}
      {data && overall && (
        <div
          className={`flex items-center gap-2 rounded-md border-l-4 px-4 py-3 text-sm ${
            data.status === "ok"
              ? "border-green-500 bg-green-50 text-green-900"
              : data.status === "warning"
                ? "border-amber-500 bg-amber-50 text-amber-900"
                : "border-red-500 bg-red-50 text-red-900"
          }`}
          data-testid="diagnostics-overall"
        >
          <overall.icon className="h-4 w-4" />
          <span className="font-medium">
            {t("diagnostics.overall")} : {overall.label}
          </span>
          <span className="text-xs opacity-80">
            — {data.counts.ok} {t("diagnostics.count_ok")},{" "}
            {data.counts.warning} {t("diagnostics.count_warning")}, {data.counts.error}{" "}
            {t("diagnostics.count_error")}
          </span>
          <span className="ml-auto text-xs opacity-70">
            {new Date(data.checked_at).toLocaleString("fr-FR")} ({data.duration_ms} ms)
          </span>
        </div>
      )}

      {/* Liste des contrôles */}
      {report.isLoading ? (
        <Card>
          <CardContent className="pt-6 text-center text-sm text-muted-foreground">
            {t("common.loading")}
          </CardContent>
        </Card>
      ) : report.isError ? (
        <Card>
          <CardContent className="pt-6 text-center text-sm text-red-600">
            <AlertCircle className="mx-auto mb-2 h-6 w-6" />
            {(report.error as Error)?.message}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {data?.checks.map((check) => {
            const style = STATUS_STYLE[check.status] ?? STATUS_STYLE.warning;
            const Icon = style.icon;
            return (
              <Card key={check.key} data-testid={`diagnostic-check-${check.key}`}>
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.className}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{check.label}</span>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                            check.status === "ok"
                              ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40"
                              : check.status === "warning"
                                ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/40"
                                : "bg-destructive/15 text-destructive dark:text-red-300 border border-destructive/40"
                          }`}
                          data-testid={`diagnostic-status-${check.key}`}
                        >
                          {style.label}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{check.message}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        {ht(
          "Les contrôles sont exécutés à la demande : cliquez sur « Refresh » pour re-tester.",
        )}
      </p>
    </div>
  );
}
